/**
 * Allocate funds: the client moves free cash between the live account and a managed account
 * (MASTER_TRADES_VISIBILITY_PLAN.md). Opened from the account bar, beside the balance.
 */
import { useState } from 'react';
import { COPY_LIMITS, D, fmtUsd, type ManagedAccount } from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { reloadManaged } from '../stores/managed';
import { useActiveAccount } from '../stores/session';
import { NumInput } from './NumInput';
import { cx, Modal } from './ui';

type Dir = 'add' | 'return';

/** Cash that can come back: not held by open managed trades, and the copy minimum stays in. */
export function returnable(m: ManagedAccount): D {
  return D.max(D.ZERO, D.min(D.from(m.account.withdrawable), D.from(m.account.equity).sub(COPY_LIMITS.minAmount)));
}

export function AllocateFundsDialog({ accounts, onClose }: { accounts: ManagedAccount[]; onClose: () => void }) {
  const live = useActiveAccount();
  const [copyId, setCopyId] = useState(accounts[0]?.copy.id ?? '');
  const [dir, setDir] = useState<Dir>('add');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const m = accounts.find((a) => a.copy.id === copyId) ?? accounts[0];
  if (!m || !live) return null;

  const available = dir === 'add' ? D.max(D.ZERO, D.from(live.withdrawable)) : returnable(m);
  const amt = amount && D.isValid(amount) ? D.from(amount) : null;
  const now = D.from(m.account.equity);
  const after = amt ? (dir === 'add' ? now.add(amt) : now.sub(amt)) : null;
  const tooMuch = !!amt && amt.gt(available);
  const name = m.copy.managedBy?.name ?? m.copy.leader.displayName;

  const submit = async () => {
    if (!amt) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/copy/copies/${m.copy.id}/funds`, { amount: `${dir === 'add' ? '' : '-'}${amt.toFixed(2)}` });
      await reloadManaged();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const pick = (d: Dir) => {
    setDir(d);
    setAmount('');
    setErr(null);
  };

  return (
    <Modal open onClose={onClose} title={`Allocate funds · ${name}`} width={420}>
      {accounts.length > 1 && (
        <select id="allocate-account" aria-label="Managed account" value={copyId} onChange={(e) => setCopyId(e.target.value)} className="h-10 rounded-lg border border-line bg-bg px-3 text-[14px]">
          {accounts.map((a) => (
            <option key={a.copy.id} value={a.copy.id}>
              {a.copy.managedBy?.name ?? a.copy.leader.displayName}
            </option>
          ))}
        </select>
      )}
      <div role="tablist" className="grid grid-cols-2 rounded-full bg-raised p-[3px] text-[13px] font-semibold">
        {(
          [
            ['add', 'Add to managed'],
            ['return', 'Return to balance'],
          ] as const
        ).map(([d, label]) => (
          <button key={d} role="tab" type="button" aria-selected={dir === d} onClick={() => pick(d)} className={cx('h-8 rounded-full', dir === d ? 'bg-bg text-fg shadow-sm' : 'text-muted')}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex h-11 items-center gap-2 rounded-lg border border-line bg-bg px-3 focus-within:border-line-strong">
        <NumInput id="allocate-amount" autoFocus value={amount} onValue={(v) => /^\d*(\.\d{0,2})?$/.test(v) && setAmount(v)} placeholder="0.00" className="num min-w-0 flex-1 bg-transparent text-[16px] outline-none" />
        <span className="num text-[13px] font-semibold text-muted">USDT</span>
        <button type="button" onClick={() => setAmount(D.max(D.ZERO, available).toFixed(2))} className="text-[12px] font-semibold text-accent-fg">
          Max
        </button>
      </div>
      <dl className="num m-0 flex flex-col gap-1.5 text-[13px]">
        <div className="flex justify-between gap-4">
          <dt className="font-sans text-muted">{dir === 'add' ? 'Available on trading account' : 'Free to return'}</dt>
          <dd className={cx('m-0', tooMuch && 'text-down')}>{fmtUsd(available)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="font-sans text-muted">Managed account now</dt>
          <dd className="m-0">{fmtUsd(now)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="font-sans text-muted">Managed account after</dt>
          <dd className="m-0 font-semibold">{after ? fmtUsd(after) : '—'}</dd>
        </div>
      </dl>
      <p className="m-0 text-[12.5px] text-muted">
        {dir === 'add'
          ? 'Future managed trades are sized to the new amount. Trades already open keep their size.'
          : `Only cash not held by open managed trades can come back, and at least ${COPY_LIMITS.minAmount} USDT stays in the managed account.`}
      </p>
      {err && <p className="m-0 text-[13px] text-down">{err}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="h-10 rounded-lg border border-line-strong px-4 font-semibold">
          Cancel
        </button>
        <button type="button" disabled={!amt || !amt.isPos() || tooMuch || busy} onClick={() => void submit()} className="h-10 rounded-lg bg-accent px-4 font-semibold text-white disabled:opacity-50">
          {busy ? 'Saving…' : dir === 'add' ? `Allocate ${amt?.isPos() ? fmtUsd(amt) : ''}`.trim() : `Return ${amt?.isPos() ? fmtUsd(amt) : ''}`.trim()}
        </button>
      </div>
    </Modal>
  );
}
